import axios from 'axios'
import dayjs from 'dayjs'
import CryptoJS from 'crypto-js'
import {envBackHost} from 'GLOBAL/envVariables'
import cookies from 'js-cookie'
import * as React from 'react'
import {getSignalConfig, gradeIndexOf} from 'utils/config/signalConfig'
import {globalConst, signalGradeEnum} from 'utils/const/globalConst'

/**
 * RSRP(전파세기)와 RSRQ(전파품질) 조합으로 신호 등급을 판정한다.
 *
 * | RSRQ \ RSRP  | ≥-90     | -90~-100 | -100~-110 | <-110    |
 * |--------------|----------|----------|-----------|----------|
 * | ≥-10         | 양호     | 양호     | 양호      | 보통     |
 * | -10 ~ -13    | 양호     | 양호     | 보통      | 확인필요 |
 * | -13 ~ -16    | 보통     | 보통     | 보통      | 확인필요 |
 * | < -16        | 확인필요 | 확인필요 | 확인필요  | 확인필요 |
 */
export const getRsrpColor = (rsrp: number | null | undefined): string => {
  if (rsrp == null) return '#9e9e9e'
  if (rsrp >= globalConst.rssiWarningdBm) return '#2e7d32'  // 좋음  ≥ -90
  if (rsrp >= globalConst.rsrpWarning) return '#f57c00'     // 보통  -90 ~ -100
  if (rsrp >= globalConst.rsrpBad) return '#d32f2f'         // 나쁨  -100 ~ -110
  return '#7b1fa2'                                           // 불량  < -110
}

export const getRsrqColor = (rsrq: number | null | undefined): string => {
  if (rsrq == null) return '#9e9e9e'
  if (rsrq >= globalConst.rsrqGood) return '#2e7d32'          // 좋음  ≥ -10
  if (rsrq >= globalConst.rsrqInstallWarning) return '#f57c00' // 보통  -10 ~ -13
  if (rsrq >= globalConst.rsrqInstallBad) return '#d32f2f'    // 나쁨  -13 ~ -16
  return '#7b1fa2'                                              // 불량  < -16
}

/** 신호 등급을 판정할 수 없는 상태(미수신 등) */
const isSignalUnknown = (rsrp: number | null | undefined, rsrq: number | null | undefined) =>
  rsrp == null || rsrq == null || rsrp === 0 || rsrq === 0

/**
 * 신호 등급 판정. 값이 없으면 null 을 반환해 '판정 불가'와 '확인필요'를 구분한다.
 */
export const getSignalGrade = (
  rsrp: number | null | undefined,
  rsrq: number | null | undefined
): signalGradeEnum | null => {
  if (isSignalUnknown(rsrp, rsrq)) return null
  return getInstallDecision(rsrp as number, rsrq as number)
}

export const getInstallDecisionColor = (rsrp: number | null | undefined, rsrq: number | null | undefined): string => {
  const grade = getSignalGrade(rsrp, rsrq)
  switch (grade) {
    case signalGradeEnum.GOOD: return '#2e7d32'
    case signalGradeEnum.NORMAL: return '#f57c00'
    case signalGradeEnum.NEED_CHECK: return '#d32f2f'
    default: return '#9e9e9e'
  }
}

export const getInstallDecision = (rsrp: number, rsrq: number): signalGradeEnum => {
  const {rsrpThresholds, rsrqThresholds, matrix} = getSignalConfig()
  const row = gradeIndexOf(rsrq, rsrqThresholds)
  const col = gradeIndexOf(rsrp, rsrpThresholds)

  /* 설정 파일이 잘못돼 표 범위를 벗어나면 가장 보수적인 판정으로 떨어뜨린다 */
  return matrix[row]?.[col] ?? signalGradeEnum.NEED_CHECK
}

export const handleCellClick = (event: any) => {
  event.stopPropagation()
}
export const resetHandler = (...stateUpdateFunctions: any) => {
  stateUpdateFunctions.forEach((updateFunction: any) => {
    if (typeof updateFunction === 'function') {
      updateFunction()
    }
  })
}
type UpdateFunction<T> = (data: T) => void

export const onRowsSelectionHandler = (
  ids: Array<string | number>,
  updateFunctions: Array<[UpdateFunction<any>, any]>
) => {
  updateFunctions.forEach(([updateFunction, data]) => {
    if (typeof updateFunction === 'function') {
      updateFunction(data)
    }
  })
}
export const imageDetailModalOpen = (callback: any) => {
  return callback(true)
}
export const imageDetailModalClose = (callback: any) => {
  return callback(false)
}
export const deviceDetailModalOpen = (callback: any) => {
  return callback(true)
}
export const deviceDetailModalClose = (callback: any) => {
  return callback(false)
}
export const customerDetailModalOpen = (callback: any) => {
  return callback(true)
}
export const customerDetailModalClose = (callback: any) => {
  return callback(false)
}

export const correctModalClose = (callback: any) => {
  return callback(false)
}

export const photoModalOpen = (callback: any) => {
  return callback(true)
}

export const photoModalClose = (callback: any) => {
  return callback(false)
}

export const scrollTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

export const loginCookieAction = async (token: string) => {
  return cookies.set('license_youngit', token, {
    expires: 1,
    path: '/',
  })
}

export const removeLoginCookieAction = async () => {
  return cookies.remove('license_youngit')
}

export const loginHashAction = async (values: any) => {
  const lastTwoDigits = values.id.slice(-2)
  const userId = 'yit' + lastTwoDigits
  const salt = generateSaltBasedOnUserId(userId)
  const hashedPassword = hashPassword(values?.password, salt)
  const _res = await axios.post(`${envBackHost}/login`, {
    id: values.id,
    password: hashedPassword,
  })

  return _res?.data.res
}
export function hashPassword(password: any, salt: any) {
  return CryptoJS.SHA512(password + salt)?.toString()
}

export function generateSaltBasedOnUserId(userId: string) {
  return CryptoJS.SHA256(userId).toString()
}
export const hashPasswordParser = async (id: any, pw: any) => {
  const lastTwoDigits = id?.slice(-2)
  const userId = 'yit' + lastTwoDigits
  const salt = generateSaltBasedOnUserId(userId)
  // const hashedPassword = hashPassword(pw?.password, salt)

  return hashPassword(pw, salt)
}
export interface TabPanelProps {
  id?: string
  children?: React.ReactNode
  index: number
  value: number
}

export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

export const tapHandleChange = (newValue: any, callback: any) => {
  callback(newValue)
}

export const convertImageToBase64 = (url: any, callback: any) => {
  var xhr = new XMLHttpRequest()
  xhr.onload = function () {
    var reader = new FileReader()
    reader.onloadend = function () {
      callback(reader.result)
    }
    reader.readAsDataURL(xhr.response)
  }
  xhr.open('GET', url)
  xhr.responseType = 'blob'
  xhr.send()
}

export const windowWidthResize = (size: number) => {
  if (size > 1920) {
    return 20
  } else if (size > 1800) {
    return 19.5
  } else if (size > 1700) {
    return 18
  } else if (size > 1500) {
    return 16
  } else if (size > 1200) {
    return 15
  } else {
    return 13
  }
}

/**
 * 날짜 표시용 포맷. 값이 없거나 파싱되지 않으면 빈 문자열을 돌려준다.
 * dayjs(undefined) 는 현재 시각으로 해석되기 때문에, 값이 없는 항목에
 * 오늘 날짜가 찍히는 것을 막으려면 반드시 값 존재 여부를 먼저 봐야 한다.
 */
export const formatDateOrEmpty = (value: any, format = 'YYYY-MM-DD') => {
  if (!value) return ''
  const date = dayjs(value)
  return date.isValid() ? date.format(format) : ''
}
